/**
 * State del Template Designer (B.6.d.2 + B.6.d.3.b' tree refactor).
 *
 * Single source of truth: una `ArtifactDefinition` completa en memoria
 * + el `selectedSectionId` que indica qué nodo está abierto en el
 * editor central. El árbol y el editor leen del mismo state.
 *
 * **Modelo de árbol (Commit B):** las secciones son recursivas. Cada
 * nodo puede tener `children`. Solo `fixed_heading` actúa como
 * contenedor. Combinada con `repeatable: true`, un `fixed_heading` con
 * hijos se vuelve un grupo repetible: en runtime el usuario instancia
 * N copias del subárbol completo.
 *
 * Patrón: `useReducer` para mantener mutaciones centralizadas y
 * predecibles. Cada acción produce una nueva `definition`. El
 * orchestrator superior (TemplateDetailView) escucha cambios y dispara
 * autosave debounced.
 *
 * Reglas:
 *   - section_id es estable y único en TODO el árbol.
 *   - i18n y structural sections se sincronizan: añadir o quitar un
 *     nodo (en cualquier nivel) actualiza ambos lados.
 *   - cambiar block_type preserva section_id y children (si compatibles)
 *     pero resetea campos específicos del tipo viejo.
 *   - solo `fixed_heading` puede tener children. Cambiar un container
 *     a un type leaf cuando ya tiene children → bloqueado.
 */

import { useReducer, type Dispatch } from 'react'

import type {
  ArtifactDefinition,
  ArtifactI18nBlock,
  ArtifactI18nSection,
  ArtifactStructuralSection,
  BlockType,
  ValidationRule,
} from '../../../api/templates'
import { editingLocale, type SupportedLocale } from '../../../i18n/config'

// ─── State ──────────────────────────────────────────────────────────────────
export interface DesignerState {
  definition: ArtifactDefinition
  /** section_id de la sección abierta en el editor central. null cuando
   * no hay nada seleccionado o cuando el árbol está vacío. */
  selectedSectionId: string | null
  /** Locale activo en el editor de i18n (es | en). */
  activeLocale: SupportedLocale
  /** Marca interna de "dirty since last save". El orchestrator usa esto
   * para decidir si dispara el autosave. Se resetea via MARK_CLEAN. */
  dirty: boolean
}

// ─── Constants ──────────────────────────────────────────────────────────────
/** Tipos que actúan como contenedor estructural (aceptan children).
 * Espejo de `CONTAINER_BLOCK_TYPES` del backend Pydantic validator. */
export const CONTAINER_BLOCK_TYPES: ReadonlySet<BlockType> = new Set<BlockType>([
  'fixed_heading',
])

export function isContainerType(type: string): boolean {
  return CONTAINER_BLOCK_TYPES.has(type as BlockType)
}

// ─── Actions ────────────────────────────────────────────────────────────────
/** Campos estructurales de una sección que edita el diseñador (los ajustes por
 * tipo de bloque incluidos, OT-0006). */
export type StructuralField =
  | 'required'
  | 'repeatable'
  | 'min_items'
  | 'max_items'
  | 'context_sections'
  | 'table_config'
  | 'predefined_keys'
  | 'default_language'
  | 'variant'

/** Cambio a una sección: `undefined` borra el campo. */
export type StructuralPatch = {
  [K in StructuralField]?: ArtifactStructuralSection[K] | undefined
}

export type DesignerAction =
  | { type: 'SET_DEFINITION'; definition: ArtifactDefinition }
  | { type: 'SELECT_SECTION'; sectionId: string | null }
  | { type: 'SET_LOCALE'; locale: SupportedLocale }
  | { type: 'MARK_CLEAN' }
  // Global
  | {
      type: 'UPDATE_GLOBAL_I18N'
      locale: SupportedLocale
      patch: Partial<
        Pick<
          ArtifactI18nBlock,
          | 'name'
          | 'description'
          | 'purpose'
          | 'tone_audience'
          | 'ai_global_instructions'
        >
      >
    }
  | { type: 'ADD_LOCALE'; locale: SupportedLocale }
  // Sections (tree-aware)
  | {
      type: 'ADD_SECTION'
      blockType: BlockType
      /** Parent en el árbol. null = top-level. */
      parentId?: string | null
    }
  | { type: 'REMOVE_SECTION'; sectionId: string }
  // Doc 06 §3.3.1: marca un container como repeatable Y asegura que su
  // primer hijo sea `plain_text required` (el discriminador). Se usa
  // desde el dialog "¿Crear identificador?" cuando el user marca
  // repetible en un container que no cumple la regla. Atómico — evita
  // estados intermedios donde repeatable=true pero discriminador falta.
  //
  // Reglas internas:
  //   - Container vacío → inserta `plain_text required` como primer hijo.
  //   - first child es `plain_text` y required=false → solo lo marca required=true.
  //   - first child existe pero NO es `plain_text` → inserta nuevo
  //     `plain_text required` en posición 0 (los demás bajan un escalón).
  | { type: 'ENSURE_REPEATABLE_DISCRIMINATOR'; sectionId: string }
  | {
      type: 'MOVE_NODE'
      sectionId: string
      /** Padre destino. null = top-level. */
      newParentId: string | null
      /** Posición dentro de los hijos del nuevo padre (o top-level). */
      position: number
    }
  | {
      type: 'UPDATE_STRUCTURAL'
      sectionId: string
      patch: StructuralPatch
    }
  | {
      type: 'UPDATE_I18N_SECTION'
      sectionId: string
      locale: SupportedLocale
      patch: Partial<
        Pick<
          ArtifactI18nSection,
          | 'title'
          | 'description'
          | 'body'
          | 'placeholder'
          | 'help_text'
          | 'ai_instructions'
          | 'column_labels'
        >
      >
    }
  | {
      type: 'RENAME_SECTION_ID'
      oldId: string
      newId: string
    }
  | { type: 'CHANGE_BLOCK_TYPE'; sectionId: string; blockType: BlockType }
  | {
      type: 'SET_VALIDATIONS'
      sectionId: string
      validations: ValidationRule[]
    }

// ─── Tree helpers (puros) ──────────────────────────────────────────────────

/** Camino al nodo: índices anidados en `children[]` desde la raíz. Top-level
 * sections[1] → [1]. sections[1].children[0].children[2] → [1, 0, 2]. */
export type NodePath = readonly number[]

/** Encuentra el path de un nodo por section_id, o null si no existe. */
export function findNodePath(
  sections: ArtifactStructuralSection[],
  sectionId: string,
): NodePath | null {
  for (let i = 0; i < sections.length; i += 1) {
    const node = sections[i]
    if (!node) continue
    if (node.section_id === sectionId) return [i]
    if (node.children && node.children.length > 0) {
      const sub = findNodePath(node.children, sectionId)
      if (sub) return [i, ...sub]
    }
  }
  return null
}

/** Lee un nodo del árbol siguiendo el path. */
export function getNodeByPath(
  sections: ArtifactStructuralSection[],
  path: NodePath,
): ArtifactStructuralSection | null {
  if (path.length === 0) return null
  const head = path[0]
  if (head === undefined) return null
  const node = sections[head]
  if (!node) return null
  if (path.length === 1) return node
  return getNodeByPath(node.children ?? [], path.slice(1))
}

/** Walks el árbol y aplica `fn(node, path)` a cada nodo. Útil para
 * recolectar section_ids, validar, etc. */
export function walkTree(
  sections: ArtifactStructuralSection[],
  fn: (node: ArtifactStructuralSection, path: NodePath) => void,
  basePath: NodePath = [],
): void {
  for (let i = 0; i < sections.length; i += 1) {
    const node = sections[i]
    if (!node) continue
    const here: NodePath = [...basePath, i]
    fn(node, here)
    if (node.children && node.children.length > 0) {
      walkTree(node.children, fn, here)
    }
  }
}

/** Busca un nodo recursivamente por section_id. */
export function findNodeBySectionId(
  sections: ArtifactStructuralSection[],
  sectionId: string,
): ArtifactStructuralSection | null {
  for (const node of sections) {
    if (node.section_id === sectionId) return node
    if (node.children && node.children.length > 0) {
      const sub = findNodeBySectionId(node.children, sectionId)
      if (sub) return sub
    }
  }
  return null
}

/** Genera un section_id único en TODO el árbol — snake_case. */
function generateSectionId(sections: ArtifactStructuralSection[]): string {
  const existing = new Set<string>()
  walkTree(sections, (n) => existing.add(n.section_id))
  let n = existing.size + 1
  while (existing.has(`section_${n}`)) n += 1
  return `section_${n}`
}

/** Convierte un texto libre en un section_id válido: lowercase, sin
 * acentos, espacios y símbolos colapsados a `_`, prefijado con letra
 * si arranca con número, max 60 chars. Matchea el pattern del backend
 * `^[a-z][a-z0-9_]*$`. */
export function slugify(text: string): string {
  const normalized = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
  if (!normalized) return ''
  // El primer char debe ser letra (a-z), no dígito.
  return /^[a-z]/.test(normalized) ? normalized : `s_${normalized}`.slice(0, 60)
}

/** Devuelve un section_id slug único en el árbol. Si el slug base
 * colisiona, agrega `_2`, `_3`, etc. `excludeId` permite que el propio
 * nodo conserve su slug actual sin contar como colisión. */
function uniqueSectionIdSlug(
  sections: ArtifactStructuralSection[],
  base: string,
  excludeId: string | null,
): string {
  if (!base) return ''
  const taken = new Set<string>()
  walkTree(sections, (n) => {
    if (n.section_id !== excludeId) taken.add(n.section_id)
  })
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}_${n}`)) n += 1
  return `${base}_${n}`
}

/** Renombra un section_id en TODA la definición: nodo estructural,
 * todas las copias en i18n.{locale}.sections[], y selección activa
 * si matchea. Devuelve null si `newId` colisiona con otro nodo
 * (caller debe abortar). */
function renameSectionIdInDefinition(
  definition: ArtifactDefinition,
  oldId: string,
  newId: string,
): ArtifactDefinition | null {
  if (oldId === newId) return definition
  // Verificar colisión: newId no debe existir en el árbol salvo que
  // sea el propio oldId.
  let collision = false
  walkTree(definition.sections, (n) => {
    if (n.section_id === newId && n.section_id !== oldId) collision = true
  })
  if (collision) return null

  const sections = mapTreeBySectionId(definition.sections, oldId, (node) => ({
    ...node,
    section_id: newId,
  }))
  const nextI18n: ArtifactDefinition['i18n'] = {}
  for (const [loc, block] of Object.entries(definition.i18n)) {
    if (!block) continue
    nextI18n[loc as SupportedLocale] = {
      ...block,
      sections: block.sections.map((s) =>
        s.section_id === oldId ? { ...s, section_id: newId } : s,
      ),
    }
  }
  return {
    ...definition,
    sections,
    i18n: nextI18n,
  }
}

/** Inmutable: produce una copia del árbol con `transform` aplicado al
 * nodo cuyo section_id matchea. Si el target tiene children y la
 * transformación devuelve null, el nodo se elimina del árbol. */
function mapTreeBySectionId(
  sections: ArtifactStructuralSection[],
  sectionId: string,
  transform: (
    node: ArtifactStructuralSection,
  ) => ArtifactStructuralSection | null,
): ArtifactStructuralSection[] {
  const out: ArtifactStructuralSection[] = []
  for (const node of sections) {
    if (node.section_id === sectionId) {
      const next = transform(node)
      if (next !== null) out.push(next)
      // si transform devuelve null → omitimos el nodo (delete)
      continue
    }
    if (node.children && node.children.length > 0) {
      const newChildren = mapTreeBySectionId(node.children, sectionId, transform)
      if (newChildren !== node.children) {
        out.push({ ...node, children: newChildren })
        continue
      }
    }
    out.push(node)
  }
  return out
}

/** Inserta un nodo bajo el padre dado (o top-level si parentId es null)
 * en la posición indicada. Ajusta los `order` de los hermanos para
 * mantener una secuencia compacta empezando en 0. */
function insertNode(
  sections: ArtifactStructuralSection[],
  parentId: string | null,
  position: number,
  newNode: ArtifactStructuralSection,
): ArtifactStructuralSection[] {
  if (parentId === null) {
    // Top-level insert.
    const next = [...sections]
    const clamped = Math.max(0, Math.min(position, next.length))
    next.splice(clamped, 0, newNode)
    return reindexSiblingOrders(next)
  }
  return sections.map((node) => {
    if (node.section_id === parentId) {
      const children = [...(node.children ?? [])]
      const clamped = Math.max(0, Math.min(position, children.length))
      children.splice(clamped, 0, newNode)
      return { ...node, children: reindexSiblingOrders(children) }
    }
    if (node.children && node.children.length > 0) {
      const newChildren = insertNode(node.children, parentId, position, newNode)
      if (newChildren !== node.children) {
        return { ...node, children: newChildren }
      }
    }
    return node
  })
}

/** Asigna orders 0..N-1 a los hermanos respetando su orden actual. */
function reindexSiblingOrders(
  siblings: ArtifactStructuralSection[],
): ArtifactStructuralSection[] {
  return siblings.map((node, idx) =>
    node.order === idx ? node : { ...node, order: idx },
  )
}

/** Verifica si `targetParentId` es descendiente de `nodeId` — para
 * prevenir ciclos al hacer drag. */
function isDescendantOf(
  sections: ArtifactStructuralSection[],
  nodeId: string,
  candidateDescendantId: string,
): boolean {
  const node = findNodeBySectionId(sections, nodeId)
  if (!node || !node.children) return false
  for (const child of node.children) {
    if (child.section_id === candidateDescendantId) return true
    if (isDescendantOf([child], child.section_id, candidateDescendantId)) {
      // Compara con el hijo y abajo.
      return false // unreachable, ver alternativa más simple
    }
    // Recurse via children directamente.
    if (
      child.children &&
      child.children.some((g) => g.section_id === candidateDescendantId)
    ) {
      return true
    }
  }
  // Búsqueda profunda explícita.
  let found = false
  walkTree(node.children, (n) => {
    if (n.section_id === candidateDescendantId) found = true
  })
  return found
}

/** Auto-numeración estilo "1.2.3" siguiendo el path del nodo (1-indexed
 * para que coincida con la convención humana). */
export function nodeNumberLabel(path: NodePath): string {
  return path.map((i) => i + 1).join('.')
}

// ─── Reducer ────────────────────────────────────────────────────────────────
export function designerReducer(
  state: DesignerState,
  action: DesignerAction,
): DesignerState {
  switch (action.type) {
    case 'SET_DEFINITION':
      // Reset desde scratch (carga inicial o post-save). NO marca dirty.
      return {
        definition: action.definition,
        selectedSectionId:
          action.definition.sections[0]?.section_id ?? null,
        // La versión nueva puede no tener el idioma que se estaba editando.
        activeLocale: editingLocale(Object.keys(action.definition.i18n), state.activeLocale),
        dirty: false,
      }
    case 'SELECT_SECTION':
      return { ...state, selectedSectionId: action.sectionId }
    case 'SET_LOCALE':
      return { ...state, activeLocale: action.locale }
    case 'MARK_CLEAN':
      return { ...state, dirty: false }

    case 'UPDATE_GLOBAL_I18N': {
      const block = state.definition.i18n[action.locale]
      const nextBlock: ArtifactI18nBlock = block
        ? { ...block, ...action.patch }
        : {
            name: action.patch.name ?? '',
            ...(action.patch.description !== undefined
              ? { description: action.patch.description }
              : {}),
            ...(action.patch.tone_audience !== undefined
              ? { tone_audience: action.patch.tone_audience }
              : {}),
            ...(action.patch.ai_global_instructions !== undefined
              ? { ai_global_instructions: action.patch.ai_global_instructions }
              : {}),
            sections: [],
          }
      return {
        ...state,
        definition: {
          ...state.definition,
          i18n: { ...state.definition.i18n, [action.locale]: nextBlock },
        },
        dirty: true,
      }
    }

    case 'ADD_LOCALE': {
      if (state.definition.i18n[action.locale]) return state
      // Crear bloque vacío + sembrar `i18n.sections[]` con una entrada
      // por sección estructural existente. Usamos section_id como título
      // placeholder (NO string vacío — el backend Pydantic exige
      // min_length=1; un autosave inmediato post-add daba 400).
      const seedSections: ArtifactI18nSection[] = []
      walkTree(state.definition.sections, (n) =>
        seedSections.push({ section_id: n.section_id, title: n.section_id }),
      )
      // Copiar `name` del locale existente como placeholder no-vacío
      // (mismo motivo: ArtifactI18nBlock.name = min_length=1). El user lo
      // traduce después; sin esto, el autosave inmediato post-toggle
      // daba 400.
      const otherLocaleName =
        state.definition.i18n.es?.name ??
        state.definition.i18n.en?.name ??
        'Untitled'
      const otherLocaleDescription =
        state.definition.i18n.es?.description ??
        state.definition.i18n.en?.description
      return {
        ...state,
        definition: {
          ...state.definition,
          i18n: {
            ...state.definition.i18n,
            [action.locale]: {
              name: otherLocaleName,
              ...(otherLocaleDescription !== undefined
                ? { description: otherLocaleDescription }
                : {}),
              sections: seedSections,
            },
          },
        },
        dirty: true,
      }
    }

    case 'ADD_SECTION': {
      const parentId = action.parentId ?? null
      const sectionId = generateSectionId(state.definition.sections)

      // Container o leaf, ambos arrancan vacíos. Antes auto-sembrábamos
      // un plain_text dentro de cada fixed_heading nuevo para satisfacer
      // un validator estricto; ahora el backend acepta containers vacíos
      // (estados intermedios durante edición son legítimos) y el user
      // puebla el grupo con el botón "+ Hijo dentro de este grupo".
      const newStructural: ArtifactStructuralSection = isContainerType(
        action.blockType,
      )
        ? {
            section_id: sectionId,
            type: action.blockType,
            order: 0, // se reasigna en insertNode
            required: false,
            children: [],
          }
        : {
            section_id: sectionId,
            type: action.blockType,
            order: 0,
            required: false,
          }

      // Insertar en el padre indicado (top-level si null), al final.
      const targetSiblings =
        parentId === null
          ? state.definition.sections
          : findNodeBySectionId(state.definition.sections, parentId)?.children ??
            []
      const sections = insertNode(
        state.definition.sections,
        parentId,
        targetSiblings.length,
        newStructural,
      )

      // Sincronizar i18n con TODOS los nuevos section_ids (parent + cualquier
      // child sembrado).
      const newSectionIds: string[] = []
      walkTree([newStructural], (n) => newSectionIds.push(n.section_id))

      const nextI18n: ArtifactDefinition['i18n'] = {}
      for (const [loc, block] of Object.entries(state.definition.i18n)) {
        if (!block) continue
        nextI18n[loc as SupportedLocale] = {
          ...block,
          sections: [
            ...block.sections,
            // Usamos section_id como título placeholder (NO ""): el
            // validador Pydantic exige min_length=1 y un autosave
            // inmediato post-add con title vacío daba 400.
            ...newSectionIds.map((id) => ({ section_id: id, title: id })),
          ],
        }
      }
      return {
        ...state,
        definition: {
          ...state.definition,
          sections,
          i18n: nextI18n,
        },
        selectedSectionId: sectionId,
        dirty: true,
      }
    }

    case 'ENSURE_REPEATABLE_DISCRIMINATOR': {
      // Doc 06 §3.3.1 — atómica: marcar container repeatable + garantizar
      // primer hijo plain_text required. Usado desde el dialog del
      // Designer cuando el user toggleó "Marcar como repetible" en un
      // container que no cumplía la regla.
      const target = findNodeBySectionId(
        state.definition.sections,
        action.sectionId,
      )
      if (!target || target.type !== 'fixed_heading') {
        return state
      }
      const children = target.children ?? []
      const first = children[0]

      // Caso 1: first child existe y es plain_text → solo marcar required.
      if (first && first.type === 'plain_text' && first.required !== true) {
        const sections = mapTreeBySectionId(
          state.definition.sections,
          action.sectionId,
          (node) => ({
            ...node,
            repeatable: true,
            children: [
              { ...first, required: true },
              ...(node.children?.slice(1) ?? []),
            ],
          }),
        )
        return { ...state, definition: { ...state.definition, sections }, dirty: true }
      }

      // Caso 2: first child ya es plain_text required → solo marcar repeatable.
      if (first && first.type === 'plain_text' && first.required === true) {
        const sections = mapTreeBySectionId(
          state.definition.sections,
          action.sectionId,
          (node) => ({ ...node, repeatable: true }),
        )
        return { ...state, definition: { ...state.definition, sections }, dirty: true }
      }

      // Caso 3: container vacío o first child no es plain_text →
      // insertar plain_text required al inicio.
      const discriminatorId = uniqueSectionIdSlug(
        state.definition.sections,
        `${target.section_id}_label`,
        null,
      )
      const newDiscriminator: ArtifactStructuralSection = {
        section_id: discriminatorId,
        type: 'plain_text',
        order: 0,
        required: true,
      }
      // Marcar repeatable + insertar el discriminador en posición 0.
      // Los demás children bajan un escalón (reindex se hace en insertNode
      // sólo si entra por ese path; aquí mapeamos a mano para mantenerlo
      // atómico).
      const sections = mapTreeBySectionId(
        state.definition.sections,
        action.sectionId,
        (node) => {
          const existing = node.children ?? []
          const reindexed = existing.map((c, idx) => ({
            ...c,
            order: idx + 1,
          }))
          return {
            ...node,
            repeatable: true,
            children: [newDiscriminator, ...reindexed],
          }
        },
      )

      // Sincronizar i18n: agregar entry del discriminador con un title
      // legible por defecto. El user puede editarlo después en el panel
      // de cada locale.
      const TITLE_BY_LOCALE: Record<SupportedLocale, string> = {
        es: 'Identificador',
        en: 'Identifier',
      }
      const nextI18n: ArtifactDefinition['i18n'] = {}
      for (const [loc, block] of Object.entries(state.definition.i18n)) {
        if (!block) continue
        nextI18n[loc as SupportedLocale] = {
          ...block,
          sections: [
            ...block.sections,
            {
              section_id: discriminatorId,
              title:
                TITLE_BY_LOCALE[loc as SupportedLocale] ?? discriminatorId,
            },
          ],
        }
      }

      return {
        ...state,
        definition: {
          ...state.definition,
          sections,
          i18n: nextI18n,
        },
        selectedSectionId: discriminatorId,
        dirty: true,
      }
    }

    case 'REMOVE_SECTION': {
      // Recolectar section_ids del subárbol que se va a borrar — para
      // limpiar también del i18n.
      const target = findNodeBySectionId(
        state.definition.sections,
        action.sectionId,
      )
      if (!target) return state
      const removedIds = new Set<string>()
      walkTree([target], (n) => removedIds.add(n.section_id))

      const sections = mapTreeBySectionId(
        state.definition.sections,
        action.sectionId,
        () => null, // delete
      )
      // Re-numerar siblings top-level si el target era top-level.
      const finalSections = reindexSiblingOrders(sections)

      const nextI18n: ArtifactDefinition['i18n'] = {}
      for (const [loc, block] of Object.entries(state.definition.i18n)) {
        if (!block) continue
        nextI18n[loc as SupportedLocale] = {
          ...block,
          sections: block.sections.filter(
            (s) => !removedIds.has(s.section_id),
          ),
        }
      }
      const nextSelected = removedIds.has(state.selectedSectionId ?? '')
        ? finalSections[0]?.section_id ?? null
        : state.selectedSectionId
      return {
        ...state,
        definition: {
          ...state.definition,
          sections: finalSections,
          i18n: nextI18n,
        },
        selectedSectionId: nextSelected,
        dirty: true,
      }
    }

    case 'MOVE_NODE': {
      // Reglas:
      // 1. No se puede mover un nodo dentro de sí mismo o sus descendientes.
      // 2. Solo containers aceptan drops.
      const target = findNodeBySectionId(
        state.definition.sections,
        action.sectionId,
      )
      if (!target) return state

      if (action.newParentId !== null) {
        if (action.newParentId === action.sectionId) return state
        if (
          isDescendantOf(
            state.definition.sections,
            action.sectionId,
            action.newParentId,
          )
        ) {
          return state
        }
        const newParent = findNodeBySectionId(
          state.definition.sections,
          action.newParentId,
        )
        if (!newParent || !isContainerType(newParent.type)) return state
      }

      // Detach: lo extraemos del árbol actual.
      const withoutNode = mapTreeBySectionId(
        state.definition.sections,
        action.sectionId,
        () => null,
      )
      // Re-insertar en el destino. La copia preserva el subárbol completo.
      const reinserted = insertNode(
        withoutNode,
        action.newParentId,
        action.position,
        target,
      )
      // Re-numerar top-level por consistencia (insertNode ya lo hace
      // dentro de cada nivel afectado, pero re-aplicamos al top por si
      // el nodo se sacó/metió de top-level).
      const finalSections = reindexSiblingOrders(reinserted)
      return {
        ...state,
        definition: { ...state.definition, sections: finalSections },
        dirty: true,
      }
    }

    case 'UPDATE_STRUCTURAL': {
      const sections = mapTreeBySectionId(
        state.definition.sections,
        action.sectionId,
        (node) => {
          // `undefined` en el patch borra el campo (exactOptionalPropertyTypes).
          const patched: ArtifactStructuralSection = { ...node }
          for (const key of Object.keys(action.patch) as StructuralField[]) {
            const value = action.patch[key]
            if (value === undefined) {
              delete patched[key]
            } else {
              Object.assign(patched, { [key]: value })
            }
          }
          // Special case: si repeatable se apaga, limpiar min/max_items
          // aunque no vengan explícitamente en el patch.
          if (action.patch.repeatable === false) {
            delete patched.min_items
            delete patched.max_items
          }
          return patched
        },
      )
      return {
        ...state,
        definition: { ...state.definition, sections },
        dirty: true,
      }
    }

    case 'UPDATE_I18N_SECTION': {
      const block = state.definition.i18n[action.locale]
      if (!block) return state
      const sections = block.sections.map((s) =>
        s.section_id === action.sectionId ? { ...s, ...action.patch } : s,
      )
      let nextDefinition: ArtifactDefinition = {
        ...state.definition,
        i18n: {
          ...state.definition.i18n,
          [action.locale]: { ...block, sections },
        },
      }

      // Auto-slug del section_id desde el título. Se dispara en CADA
      // cambio del título mientras el nodo no es de la rama publicada
      // (la versión actual es draft — el caller usa readOnly para
      // bloquear edición de versiones publicadas). Si el slug del
      // título nuevo difiere del section_id actual, se hace rename
      // atómico (estructural + i18n + selección activa). Colisiones se
      // resuelven sufijando _2 / _3.
      //
      // ANTES (bug): solo se disparaba mientras section_id matchara el
      // patrón `section_N`, así que se quedaba congelado en la primera
      // letra del título (la "P" de "Pares" → section_id="p" para
      // siempre). Reportado por el user en la sesión.
      let nextSelectedSectionId = state.selectedSectionId
      if (
        typeof action.patch.title === 'string' &&
        action.patch.title.trim()
      ) {
        const baseSlug = slugify(action.patch.title)
        if (baseSlug) {
          const newId = uniqueSectionIdSlug(
            nextDefinition.sections,
            baseSlug,
            action.sectionId,
          )
          if (newId && newId !== action.sectionId) {
            const renamed = renameSectionIdInDefinition(
              nextDefinition,
              action.sectionId,
              newId,
            )
            if (renamed) {
              nextDefinition = renamed
              if (state.selectedSectionId === action.sectionId) {
                nextSelectedSectionId = newId
              }
            }
          }
        }
      }

      return {
        ...state,
        definition: nextDefinition,
        selectedSectionId: nextSelectedSectionId,
        dirty: true,
      }
    }

    case 'RENAME_SECTION_ID': {
      // Manual rename del section_id (validado por el caller: pattern
      // snake_case + unicidad). Aquí confiamos en que el caller hizo el
      // check de pattern; verificamos colisión.
      const renamed = renameSectionIdInDefinition(
        state.definition,
        action.oldId,
        action.newId,
      )
      if (!renamed) return state // colisión silenciosa — el caller debe avisar.
      return {
        ...state,
        definition: renamed,
        selectedSectionId:
          state.selectedSectionId === action.oldId
            ? action.newId
            : state.selectedSectionId,
        dirty: true,
      }
    }

    case 'CHANGE_BLOCK_TYPE': {
      // Reglas:
      // - Si el nuevo tipo es contenedor → preservamos children del nodo
      //   anterior (pueden seguir vivos).
      // - Si el nuevo tipo es hoja → bloqueamos el cambio cuando el nodo
      //   tiene children (el user debe vaciar primero). Devolvemos state
      //   sin tocar.
      const target = findNodeBySectionId(
        state.definition.sections,
        action.sectionId,
      )
      if (!target) return state
      const targetIsContainer = isContainerType(action.blockType)
      const hadChildren = (target.children ?? []).length > 0
      if (!targetIsContainer && hadChildren) {
        return state // bloqueado — el caller debería avisar al user.
      }

      const sections = mapTreeBySectionId(
        state.definition.sections,
        action.sectionId,
        (node) => {
          const next: ArtifactStructuralSection = {
            section_id: node.section_id,
            type: action.blockType,
            order: node.order,
            validation: node.validation ?? [],
          }
          if (node.required !== undefined) next.required = node.required
          if (node.repeatable !== undefined) next.repeatable = node.repeatable
          if (node.min_items !== undefined) next.min_items = node.min_items
          if (node.max_items !== undefined) next.max_items = node.max_items
          if (targetIsContainer && node.children) {
            next.children = node.children
          }
          return next
        },
      )
      return {
        ...state,
        definition: { ...state.definition, sections },
        dirty: true,
      }
    }

    case 'SET_VALIDATIONS': {
      const sections = mapTreeBySectionId(
        state.definition.sections,
        action.sectionId,
        (node) => ({ ...node, validation: action.validations }),
      )
      return {
        ...state,
        definition: { ...state.definition, sections },
        dirty: true,
      }
    }

    default:
      return state
  }
}

// ─── Hook ──────────────────────────────────────────────────────────────────
export function useTemplateDesignerState(
  initial: ArtifactDefinition,
  defaultLocale: SupportedLocale = 'es',
): [DesignerState, Dispatch<DesignerAction>] {
  const [state, dispatch] = useReducer(designerReducer, undefined, () => ({
    definition: initial,
    selectedSectionId: initial.sections[0]?.section_id ?? null,
    activeLocale: defaultLocale,
    dirty: false,
  }))
  return [state, dispatch]
}

// ─── Public helpers para componentes consumers ─────────────────────────────
/** Lookup de structural por section_id (recursivo). */
export function findSectionStructural(
  definition: ArtifactDefinition,
  sectionId: string,
): ArtifactStructuralSection | undefined {
  return findNodeBySectionId(definition.sections, sectionId) ?? undefined
}

export function findSectionI18n(
  definition: ArtifactDefinition,
  sectionId: string,
  locale: SupportedLocale,
): ArtifactI18nSection | undefined {
  return definition.i18n[locale]?.sections.find(
    (s) => s.section_id === sectionId,
  )
}

/** True si la sección tiene title llenado en TODOS los locales activos
 * de la plantilla. Lo usa el árbol para mostrar el badge de i18n
 * complete vs partial. */
export function isI18nComplete(
  definition: ArtifactDefinition,
  sectionId: string,
): boolean {
  const locales = Object.keys(definition.i18n) as SupportedLocale[]
  if (locales.length === 0) return false
  return locales.every((loc) => {
    const block = definition.i18n[loc]
    if (!block) return false
    const sec = block.sections.find((s) => s.section_id === sectionId)
    return Boolean(sec && sec.title.trim())
  })
}

/** Cuenta total de nodos en el árbol — para los stats globales. */
export function countAllNodes(definition: ArtifactDefinition): number {
  let n = 0
  walkTree(definition.sections, () => {
    n += 1
  })
  return n
}


// ─── Doc 24 §3 Fase 6 — validación de cross-cutting concerns ───────────────

/** Doc 24 §3.3.2 — misma regla que el backend: una sección solo toma como
 * contexto (`context_sections[]` intra-template) secciones que van antes en
 * el documento — preorden de la lista, el orden en que se presenta y se
 * genera — y que no son su propio contenedor, cuyo contenido la incluye.
 * El `order` de cada nodo es su posición entre hermanos y no sirve para
 * comparar secciones de grupos distintos. Las refs cross-template (con `.`)
 * se resuelven en runtime (ADR-0002) y no pasan por aquí.
 */
export function backwardRefCandidates(
  sections: ArtifactStructuralSection[],
  sectionId: string,
): ArtifactStructuralSection[] {
  const inDocumentOrder: { node: ArtifactStructuralSection; path: NodePath }[] = []
  walkTree(sections, (node, path) => inDocumentOrder.push({ node, path }))
  const position = inDocumentOrder.findIndex(({ node }) => node.section_id === sectionId)
  const target = inDocumentOrder[position]
  if (!target) return []
  const isAncestor = (path: NodePath) =>
    path.length < target.path.length && path.every((idx, k) => target.path[k] === idx)
  return inDocumentOrder
    .slice(0, position)
    .filter(({ path }) => !isAncestor(path))
    .map(({ node }) => node)
}

/** Doc 24 §3.3.1 — regla del discriminador: un `fixed_heading` repetible
 * requiere primer hijo `plain_text` required (label de la instancia).
 * Devuelve true si la sección viola la regla (es repetible pero el
 * primer hijo no califica). El UI usa esto para warning antes de
 * habilitar repetible en el toggle.
 */
export function isRepeatableMissingDiscriminator(
  section: ArtifactStructuralSection,
): boolean {
  if (section.type !== 'fixed_heading') return false
  if (!section.repeatable) return false
  const children = section.children ?? []
  if (children.length === 0) return true
  const first = children[0]
  if (!first) return true
  return first.type !== 'plain_text' || first.required !== true
}

// ─── Client-side validation (Fase 10.5 BUG-05) ────────────────────────────
/** Pattern snake_case que el backend Pydantic enforza en `key` de columnas
 * y `value` de enum values: `^[a-z][a-z0-9_]*$`. Si el frontend deja
 * propagar valores que no cumplen, el PATCH responde 400 y el user solo
 * ve "No se pudo guardar". Esta función pre-detecta esos casos y permite
 * al orchestrator saltar el autosave hasta que el dato sea válido. */
const SNAKE_CASE_KEY = /^[a-z][a-z0-9_]*$/

export interface ClientValidationIssue {
  /** Mensaje listo para mostrar al usuario (sin keys i18n para mantenerlo
   * directo — los detalles se enriquecen con `params`). */
  code:
    | 'invalid_column_key'
    | 'invalid_enum_value'
    | 'invalid_section_id'
    | 'missing_name'
    | 'missing_scope'
    | 'no_stages'
    | 'missing_stage_name'
    | 'missing_stage_roles'
  /** Path human-readable al campo afectado (p.ej. "section_1 › col 0"). */
  path: string
  /** Detalles para que el orchestrator componga el mensaje localizado. */
  params: Record<string, string>
}

/** Recorre el `ArtifactDefinition` y retorna issues estructurales que el
 * backend rechazaría con 400 antes de aceptar el PATCH. Solo se incluyen
 * reglas de FORMATO (patterns), no de COMPLETITUD (labels vacíos, columnas
 * vacías — esos viven en el publish-gate y SÍ deben permitir autosave).
 *
 * El orchestrator (`TemplateDetailView.persist`) llama a esta función
 * antes de cada PATCH; si retorna un array no-vacío, omite la llamada al
 * backend y muestra el primer issue al usuario. */
export function findClientValidationIssues(
  definition: ArtifactDefinition,
): ClientValidationIssue[] {
  const issues: ClientValidationIssue[] = []

  function walkSection(node: ArtifactStructuralSection, path: string): void {
    // section_id pattern — el backend Pydantic también lo enforza.
    if (node.section_id && !SNAKE_CASE_KEY.test(node.section_id)) {
      issues.push({
        code: 'invalid_section_id',
        path,
        params: { section_id: node.section_id },
      })
    }

    if (node.type === 'structured_table' && node.table_config) {
      const cols = node.table_config.columns ?? []
      cols.forEach((col, idx) => {
        const colPath = `${path} › col[${idx}] (${col.key || '?'})`
        if (col.key && !SNAKE_CASE_KEY.test(col.key)) {
          issues.push({
            code: 'invalid_column_key',
            path: colPath,
            params: { key: col.key, section_id: node.section_id },
          })
        }
        const evals = col.enum_values ?? []
        evals.forEach((ev, evIdx) => {
          if (ev.value && !SNAKE_CASE_KEY.test(ev.value)) {
            issues.push({
              code: 'invalid_enum_value',
              path: `${colPath} › enum[${evIdx}]`,
              params: {
                value: ev.value,
                key: col.key,
                section_id: node.section_id,
              },
            })
          }
        })
      })
    }

    const children = node.children ?? []
    for (const child of children) {
      walkSection(child, `${path} › ${child.section_id || '?'}`)
    }
  }

  for (const top of definition.sections ?? []) {
    walkSection(top, top.section_id || '?')
  }

  return issues
}
